/** Simplified version diff — SPEC-2 §6. */

import { Minus, Plus, PencilLine } from "lucide-react";

import type { MethodVersionOut } from "@/api/types";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { Markdown } from "@/lib/markdown";

type Change = "added" | "removed" | "changed" | "same";

const SECTIONS = [
  { key: "preconditions_md", label: "Когда применять" },
  { key: "body_md", label: "Шаги" },
  { key: "checks_md", label: "Проверка" },
  { key: "pitfalls_md", label: "Ограничения" },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

function classify(before: string | null, after: string | null): Change {
  const a = (before ?? "").trim();
  const b = (after ?? "").trim();
  if (a === b) return "same";
  if (!a) return "added";
  if (!b) return "removed";
  return "changed";
}

const CHANGE_META: Record<Exclude<Change, "same">, { label: string; icon: typeof Plus }> = {
  added: { label: "добавилось", icon: Plus },
  removed: { label: "убрали", icon: Minus },
  changed: { label: "изменилось", icon: PencilLine },
};

/**
 * Section-level diff, not word-level — and that is the design, not a shortcut.
 *
 * A word diff of prose invites reading the punctuation instead of the change:
 * it highlights every reworded sentence with the same emphasis as a step that
 * was actually removed. What a person needs from two versions of a method is
 * "which parts moved", answered in one glance; the text itself is right there
 * to read if the answer is interesting.
 *
 * Unchanged sections are collapsed to a single line for the same reason — four
 * expanded sections where one changed hides the change.
 */
export function VersionDiff({
  before,
  after,
}: {
  before: MethodVersionOut;
  after: MethodVersionOut;
}) {
  const changes = SECTIONS.map((section) => ({
    ...section,
    change: classify(before[section.key as SectionKey], after[section.key as SectionKey]),
    beforeText: before[section.key as SectionKey],
    afterText: after[section.key as SectionKey],
  }));
  const moved = changes.filter((c) => c.change !== "same");

  return (
    <div className="flex flex-col gap-3">
      <p className="text-meta text-hint">
        v{before.number} → v{after.number}
        {moved.length === 0 && " · текст не менялся"}
      </p>

      {after.change_note && (
        <p className="rounded-card bg-sbg p-3 text-cell text-text">{after.change_note}</p>
      )}

      {changes.map((section) => {
        if (section.change === "same") {
          return (
            <p key={section.key} className="text-meta text-hint">
              {section.label} — без изменений
            </p>
          );
        }
        const meta = CHANGE_META[section.change];
        const Icon = meta.icon;
        return (
          <section key={section.key} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <h3 className="text-section font-bold text-text">{section.label}</h3>
              <Badge
                tone={section.change === "removed" ? "danger" : "accent"}
                icon={<Icon className="h-3 w-3" aria-hidden="true" />}
              >
                {meta.label}
              </Badge>
            </div>
            {section.change !== "added" && (
              <div className="rounded-card border border-sep p-3">
                <p className="mb-1 text-micro uppercase tracking-[.06em] text-hint">
                  было (v{before.number})
                </p>
                <p className="whitespace-pre-wrap text-cell text-hint">
                  {section.beforeText || "—"}
                </p>
              </div>
            )}
            {section.change !== "removed" && (
              <div className={cn("rounded-card bg-accent-soft p-3")}>
                <p className="mb-1 text-micro uppercase tracking-[.06em] text-hint">
                  стало (v{after.number})
                </p>
                <p className="whitespace-pre-wrap text-cell text-text">
                  {section.afterText || "—"}
                </p>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** The four named sections of a version, for the Инструкция tab. */
export function VersionSections({ version }: { version: MethodVersionOut }) {
  return (
    <div className="flex flex-col gap-4">
      {SECTIONS.map((section) => {
        const text = version[section.key as SectionKey];
        if (!text?.trim()) {
          // Saying a section is empty beats omitting it: "no limitations
          // recorded" and "we never wrote any down" look the same otherwise,
          // and only one of them is reassuring.
          return (
            <section key={section.key} className="flex flex-col gap-1">
              <h3 className="text-section font-bold text-text">{section.label}</h3>
              <p className="text-meta text-hint">не заполнено</p>
            </section>
          );
        }
        return (
          <section key={section.key} className="flex flex-col gap-1">
            <h3 className="text-section font-bold text-text">{section.label}</h3>
            <Markdown className="text-cell text-text" text={text} />
          </section>
        );
      })}
    </div>
  );
}
